import { Component, OnInit, ViewEncapsulation, Output, EventEmitter } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { DestructorComponent } from 'src/app/components/destructor/destructor.component';
import { ClientService } from 'src/app/services/user/client.service';
import { CreditDebitNotesModalService } from 'src/app/services/credit-debit-notes-modal/credit-debit-notes-modal.service';
import { CreditDebitNotesService } from 'src/app/services/credit-debit-notes/credit-debit-notes.service';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import Swal from 'sweetalert2';

declare var bootstrap: any;

@Component({
  selector: 'app-add-update-credit-debit-notes',
  templateUrl: './add-update-credit-debit-notes.component.html',
  styleUrls: ['./add-update-credit-debit-notes.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class AddUpdateCreditDebitNotesComponent extends DestructorComponent implements OnInit {

  @Output() creditDebitNoteAdded = new EventEmitter<void>();
  @Output() creditDebitNoteUpdated = new EventEmitter<void>();

  creditDebitNoteForm: FormGroup;
  isLoading: boolean = false;
  isEditMode: boolean = false;
  selectedCreditDebitNote: any = null;

  // Customer search properties
  customerSearchTerm: string = '';
  customerSearchResults: any[] = [];
  isCustomerSearching: boolean = false;
  showCustomerDropdown: boolean = false;
  selectedCustomer: any = null;
  private customerSearchSubject = new Subject<string>();

  constructor(
    private fb: FormBuilder,
    private _clientService: ClientService,
    private _creditDebitNotesModalService: CreditDebitNotesModalService,
    private _creditDebitNotesService: CreditDebitNotesService
  ) {
    super();
    this.initializeForm();
  }

  ngOnInit(): void {
    this.setupCustomerSearchDebounce();
    this.setupModalListener();
  }

  initializeForm(): void {
    this.creditDebitNoteForm = this.fb.group({
      customer_id: ['', [Validators.required]],
      customer_name: ['', [Validators.required]],
      invoice_no: ['', [Validators.required]],
      date: ['', [Validators.required]],
      type: ['', [Validators.required]],
      amount: ['', [Validators.required, Validators.min(0)]],
      brief: ['', [Validators.required, Validators.maxLength(250)]]
    });
  }

  // Customer search methods
  private setupCustomerSearchDebounce(): void {
    this.customerSearchSubject.pipe(
      debounceTime(300),
      distinctUntilChanged()
    ).subscribe(searchTerm => {
        this.searchCustomers(searchTerm);
    });
  }

  onCustomerSearchInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.customerSearchTerm = target.value;
    this.selectedCustomer = null;

    // Update the form control with the typed value
    this.creditDebitNoteForm.patchValue({
      customer_name: this.customerSearchTerm
    });
    this.customerSearchSubject.next(this.customerSearchTerm);
    this.showCustomerDropdown = true;
  }

  private searchCustomers(searchTerm: string): void {
    this.isCustomerSearching = true;
    this.subscriptions.push(
      this._clientService.GetData('1', searchTerm).subscribe({
        next: (response: any) => {
          this.isCustomerSearching = false;
          if (response.status && response.data && response.data.data) {
            this.customerSearchResults = response.data.data;
            this.showCustomerDropdown = this.customerSearchResults.length > 0;
          } else {
            this.customerSearchResults = [];
            this.showCustomerDropdown = false;
          }
        },
        error: (error: any) => {
          this.isCustomerSearching = false;
          console.error('Error searching customers:', error);
          this.customerSearchResults = [];
          this.showCustomerDropdown = false;
        }
      })
    );
  }

  selectCustomer(customer: any): void {
    this.selectedCustomer = customer;
    const customerName = this.getCustomerDisplayName(customer);
    this.customerSearchTerm = customerName;
    this.showCustomerDropdown = false;
    this.customerSearchResults = [];

    // Update the form with customer data
    this.creditDebitNoteForm.patchValue({
      customer_id: customer._id || customer.id || '',
      customer_name: customerName
    });
  }

  clearCustomerSearch(): void {
    this.customerSearchTerm = '';
    this.selectedCustomer = null;
    this.showCustomerDropdown = false;
    this.customerSearchResults = [];

    // Reset customer fields
    this.creditDebitNoteForm.patchValue({
      customer_id: '',
      customer_name: ''
    });
  }

  onCustomerInputFocus(): void {
    if (this.customerSearchResults.length > 0) {
      this.showCustomerDropdown = true;
    }
  }

  onCustomerInputBlur(): void {
    // Delay hiding dropdown to allow for click events
    setTimeout(() => {
      this.showCustomerDropdown = false;
    }, 200);
  }

  getCustomerDisplayName(customer: any): string {
    if (customer.name && customer.company) {
      return `${customer.name}, ${customer.company}, ${customer.currency_type}`;
    }
    return customer.name || customer.company || '';
  }

  private setupModalListener(): void {
    const modalElement = document.getElementById('kt_modal_add_update_credit_debit_notes');
    if (modalElement) {
      modalElement.addEventListener('shown.bs.modal', () => {
        const serviceCreditDebitNote = this._creditDebitNotesModalService.getSelectedCreditDebitNote();
        if (serviceCreditDebitNote) {
          this.isEditMode = true;
          this.selectedCreditDebitNote = serviceCreditDebitNote;
          this.populateForm(this.selectedCreditDebitNote);
        } else {
          this.isEditMode = false;
          this.selectedCreditDebitNote = null;
          this.resetForm();
        }
      });

      modalElement.addEventListener('hidden.bs.modal', () => {
        this._creditDebitNotesModalService.clearSelectedCreditDebitNote();
        this.resetForm();
      });
    }
  }

  populateForm(creditDebitNote: any): void {
    this.creditDebitNoteForm.patchValue({
      customer_id: creditDebitNote.customer_id || '',
      customer_name: creditDebitNote.customer?.name || '',
      invoice_no: creditDebitNote.invoice_no || '',
      date: creditDebitNote.date || '',
      type: creditDebitNote.type || '',
      amount: creditDebitNote.amount || '',
      brief: creditDebitNote.brief || ''
    });

    if (creditDebitNote.customer) {
      this.selectedCustomer = creditDebitNote.customer;
      this.customerSearchTerm = this.getCustomerDisplayName(creditDebitNote.customer);
    }
  }

  resetForm(): void {
    this.creditDebitNoteForm.reset({
      customer_id: '',
      customer_name: '',
      invoice_no: '',
      date: '',
      type: '',
      amount: '',
      brief: ''
    });
    this.isEditMode = false;
    this.selectedCreditDebitNote = null;
    this.customerSearchTerm = '';
    this.selectedCustomer = null;
    this.customerSearchResults = [];
    this.showCustomerDropdown = false;
  }

  onSubmit(): void {
    if (this.creditDebitNoteForm.invalid) {
      this.markFormGroupTouched(this.creditDebitNoteForm);
      Swal.fire({
        title: 'Validation Error!',
        text: 'Please fill in all required fields correctly.',
        icon: 'error',
        confirmButtonText: 'OK'
      });
      return;
    }

    this.isLoading = true;

    // Prepare JSON data according to backend validation schema
    const formValues = this.creditDebitNoteForm.getRawValue();
    const jsonData: any = {
      customer_id: formValues.customer_id,
      invoice_no: formValues.invoice_no,
      amount: formValues.amount,
      date: formValues.date,
      type: formValues.type,
      brief: formValues.brief
    };

    // Call API - use UpdateData if in edit mode, otherwise AddData
    const apiCall = this.isEditMode
      ? this._creditDebitNotesService.UpdateData(this.selectedCreditDebitNote._id, jsonData)
      : this._creditDebitNotesService.AddData(jsonData);

    this.subscriptions.push(
      apiCall.subscribe({
        next: (response: any) => {
          this.isLoading = false;
          if (response.status) {
            Swal.fire({
              title: 'Success!',
              text: `Credit/Debit Note ${this.isEditMode ? 'updated' : 'added'} successfully.`,
              icon: 'success',
              confirmButtonText: 'OK'
            }).then(() => {
              // Close modal
              const modalElement = document.getElementById('kt_modal_add_update_credit_debit_notes');
              if (modalElement) {
                const modal = bootstrap.Modal.getInstance(modalElement);
                if (modal) {
                  modal.hide();
                }
              }

              // Emit appropriate event
              if (this.isEditMode) {
                this.creditDebitNoteUpdated.emit();
              } else {
                this.creditDebitNoteAdded.emit();
              }
            });
          } else {
            Swal.fire({
              title: 'Error!',
              text: response.message || `Failed to ${this.isEditMode ? 'update' : 'add'} credit/debit note.`,
              icon: 'error',
              confirmButtonText: 'OK'
            });
          }
        },
        error: (error: any) => {
          this.isLoading = false;
          console.error('Error submitting credit/debit note:', error);
          Swal.fire({
            title: 'Error!',
            text: `Failed to ${this.isEditMode ? 'update' : 'add'} credit/debit note. Please try again.`,
            icon: 'error',
            confirmButtonText: 'OK'
          });
        }
      })
    );
  }

  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      control?.markAsTouched();

      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      }
    });
  }
}
